/**
 * Server addresses, in order of preference (comma-separated in VITE_API_BASE). The app uses the
 * mainland-friendly Hong Kong relay and falls back to Cloudflare only when the relay is unreachable —
 * a slow answer on a weak mobile signal is not a reason to leave it, because Cloudflare is far slower
 * from the mainland. While on a fallback it keeps checking the relay and returns as soon as it answers.
 */
const BASES = ((import.meta.env.VITE_API_BASE as string | undefined) ?? '').split(',').map((b) => b.trim().replace(/\/$/, ''));
let current = 0;
export const apiBase = () => BASES[current] ?? '';
const TOKEN_KEY = 'engpod.token';

/** Whether a server answers at all (a tiny request, so it is quick even on a slow line). */
async function reachable(base: string, ms: number) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    return (await fetch(`${base}/api/health`, { cache: 'no-store', signal: controller.signal })).ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

async function backToPreferred() {
  if (current > 0 && (await reachable(BASES[0]!, 5_000))) {
    current = 0;
    clearInterval(recheck);
    recheck = undefined;
  }
}
let recheck: ReturnType<typeof setInterval> | undefined;
function fallBack() {
  current += 1;
  recheck ??= setInterval(() => void backToPreferred(), 60_000);
}
if (typeof document !== 'undefined')
  document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && void backToPreferred());

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

export const token = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (t: string | null) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY)),
};

/** Media and poster URLs from the API are relative; the iOS app needs them absolute. */
export const absolute = (url: string | null | undefined) => (url && url.startsWith('/') ? `${apiBase()}${url}` : (url ?? ''));

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const headers = new Headers(init.headers);
  const t = token.get();
  if (t) headers.set('authorization', `Bearer ${t}`);
  // Lets the server count study days in the learner's own time zone.
  headers.set('x-tz-offset', String(new Date().getTimezoneOffset()));
  let bodyInit = init.body;
  if (init.json !== undefined) {
    headers.set('content-type', 'application/json');
    bodyInit = JSON.stringify(init.json);
  }
  let res: Response;
  for (let attempt = 0; ; attempt++) {
    // Uploads can take long; everything else gives up instead of spinning forever (e.g. when the
    // server address is blocked on the current network).
    const fallback = current < BASES.length - 1;
    const timeout = init.body instanceof Blob ? 0 : attempt === 0 && fallback ? 10_000 : 20_000;
    const controller = new AbortController();
    const timer = timeout ? setTimeout(() => controller.abort(), timeout) : undefined;
    try {
      res = await fetch(`${apiBase()}/api${path}`, { ...init, headers, body: bodyInit, signal: init.signal ?? controller.signal });
      break;
    } catch {
      if (init.signal?.aborted) throw new ApiError(0, 'aborted', '已取消');
      const timedOut = controller.signal.aborted;
      // Slow but alive: wait longer on the same server instead of switching to a slower one.
      if (timedOut && attempt === 0 && (await reachable(apiBase(), 5_000))) continue;
      if (fallback) {
        fallBack();
        continue;
      }
      throw new ApiError(0, 'network', timedOut ? '连接超时：当前网络访问不到 ENGPOD 服务' : '网络连接失败，请检查网络');
    } finally {
      clearTimeout(timer);
    }
  }
  const data = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
  if (!res.ok) {
    if (res.status === 401 && t) token.set(null);
    throw new ApiError(res.status, data.error ?? 'error', data.message ?? `请求失败（${res.status}）`);
  }
  return data as T;
}

export const errorText = (e: unknown) => (e instanceof Error ? e.message : String(e));

// ---- Fast start on slow networks -------------------------------------------------------------
// The home screen needs several answers before it can draw. On a mobile line each request costs a
// round trip plus a CORS preflight, so screens draw from the last answer saved on the device and
// refresh in the background; GETs asked for at the same moment share one request.

const SNAP = 'engpod.snap.';
const SNAP_ORDER = `${SNAP}order`;
const SNAP_MAX = 24;
const inflight = new Map<string, Promise<unknown>>();

function shared<T>(path: string): Promise<T> {
  const key = `${token.get() ?? ''} ${path}`;
  let p = inflight.get(key) as Promise<T> | undefined;
  if (!p) {
    p = api<T>(path).finally(() => inflight.delete(key));
    inflight.set(key, p);
  }
  return p;
}

const snapKey = (path: string) => `${SNAP}${token.get() ? 'u' : 'a'}:${path}`;

function saveSnapshot(path: string, raw: string) {
  try {
    const key = snapKey(path);
    localStorage.setItem(key, raw);
    const order = [key, ...(JSON.parse(localStorage.getItem(SNAP_ORDER) ?? '[]') as string[]).filter((k) => k !== key)];
    order.slice(SNAP_MAX).forEach((k) => localStorage.removeItem(k));
    localStorage.setItem(SNAP_ORDER, JSON.stringify(order.slice(0, SNAP_MAX)));
  } catch {
    // storage full or unavailable: the snapshot is only a speed-up
  }
}

/** The last saved answer for a GET path, if any. */
export function readSnapshot<T>(path: string): T | null {
  try {
    const raw = localStorage.getItem(snapKey(path));
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

/**
 * A GET that draws twice: `snapshot` is the last saved answer (null the first time), `fresh` the
 * server's answer. `fresh.changed` is false when it equals the snapshot, so callers can skip a redraw.
 */
export function cached<T>(path: string): { snapshot: T | null; fresh: Promise<{ data: T; changed: boolean }> } {
  const before = localStorage.getItem(snapKey(path));
  const snapshot = readSnapshot<T>(path);
  const fresh = shared<T>(path).then((data) => {
    const raw = JSON.stringify(data);
    saveSnapshot(path, raw);
    return { data, changed: raw !== before };
  });
  return { snapshot, fresh };
}

/** Saved answers belong to one account: forget them when it changes. */
export function clearSnapshots() {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(SNAP))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }
}
